import {action, computed, makeObservable} from 'mobx';

import type {AudioModelFactory} from '../../../core/models/Audio/AudioModel.factory';
import type {Nullable} from '../../../utils/utility-types';

import {type AudioModel} from '../../../core/models/Audio/Audio.model';
import {secondsToMinutesAndSeconds} from '../../../utils/datetime';
import {mergeDeepObj} from '../../../utils/object/mergeDeepObj';
import {
  type Destructible,
  ReactionDisposeManager,
  type WithReactionDispose,
} from '../../../utils/store';
import {WithCallbacks} from '../../../utils/store/WithCallbacks';

export class AudioPlayerState
  implements
    Destructible,
    WithCallbacks.Interface<AudioPlayerState>,
    WithReactionDispose
{
  callbacks = new WithCallbacks.Manager<AudioPlayerState>();
  disposer = new ReactionDisposeManager();
  model: AudioModel;

  constructor(
    props: AudioPlayerState.Props,
    audioModelFactory: AudioModelFactory,
  ) {
    this.model = audioModelFactory.create({});
    this.callbacks.setCallbacks(props?.callbacks);
    this.callbacks.init(this);
    makeObservable(this, {
      renderCurrentTime: computed,
      renderDuration: computed,
      setCallbacks: action.bound,
      skipBackward: action.bound,
      skipForward: action.bound,
    });
  }

  async init({
    disableCache,
    src,
  }: AudioPlayerState.InitialProps): Promise<void> {
    return this.model.init({disableCache, src});
  }

  destruct(): void {
    this.model.destruct();
    this.disposer.dispose();
  }

  get PROGRESS_BAR_STEP() {
    return 0.01;
  }

  get renderCurrentTime() {
    return secondsToMinutesAndSeconds(this.model.currentTimeSec);
  }

  get renderDuration() {
    return secondsToMinutesAndSeconds(this.model.durationSec);
  }

  setCallbacks(callbacks: WithCallbacks.Callbacks<AudioPlayerState>) {
    mergeDeepObj(this.callbacks, callbacks);
  }

  async skipBackward() {
    const next = Math.max(this.model.currentTime - this.STEP_SECONDS, 0);

    return this.model.seek(next);
  }

  async skipForward() {
    const next = Math.min(
      this.model.currentTime + this.STEP_SECONDS,
      this.model.durationSec,
    );

    return this.model.seek(next);
  }

  private get STEP_SECONDS() {
    return 30;
  }
}

export namespace AudioPlayerState {
  export type Callbacks = WithCallbacks.Callbacks<AudioPlayerState>;
  export type InitialProps = {
    disableCache?: boolean;
    src: Nullable<string>;
  };

  export type Props = {
    callbacks?: Callbacks;
  };
}
